import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetAllHiringAssessments } from 'src/hooks/hiringAssessment.hooks';
import NewCreatingPositionLoader from '../position/NewCreatingPositionLoader';
import { Spinner } from 'flowbite-react';
import { MdOutlineErrorOutline } from 'react-icons/md';
import { Box } from '@mui/material';

interface DataItem {
	label: string;
	value: number;
	styleMatch?:boolean
}

interface FunnelChartProps {
	data: DataItem[];
	graphTitle?: string;
}

// Add this custom hook at the top of the file
const useCountAnimation = (targetValue: number, duration: number = 1000) => {
	const [count, setCount] = useState(0);

	useEffect(() => {
		let startTime: number;
		const startValue = count;

		const animate = (currentTime: number) => {
			if (!startTime) startTime = currentTime;
			const progress = Math.min((currentTime - startTime) / duration, 1);

			setCount(Math.floor(startValue + (targetValue - startValue) * progress));

			if (progress < 1) {
				requestAnimationFrame(animate);
			}
		};

		requestAnimationFrame(animate);
	}, [targetValue]);

	return count;
};

// FunnelBar component to handle individual bars
const FunnelBar: React.FC<{ item: DataItem; maxValue: number }> = ({ item, maxValue }) => {
	const animatedValue = useCountAnimation(item.value, 200);

	// Calculate height percentage
	const heightPercentage =
		maxValue === 0
			? 0
			: (() => {
					if (animatedValue === 0) return 0;
					const basePercentage = (animatedValue / maxValue) * 100;
					return basePercentage < 10 ? Math.max(10, basePercentage) : basePercentage;
			  })();

	// Check if this is the tallest bar
	const isTallestBar = animatedValue === maxValue && maxValue > 0;

	return (
		<div className="flex flex-col flex-1 border-l border-[#DBDBDB] gap-6">
			<div className={`pl-2 md:pl-4 pb-2 w-full ${item.styleMatch?"h-[74px] md:h-auto":""}`}>
				<div className="text-xs md:text-sm text-gray-600 mb-1">{item.label}</div>
				<div className={`text-lg md:text-3xl font-bold ${item.styleMatch?"mt-[19px] md:mt-0":""}`}>{animatedValue.toLocaleString()}</div>
			</div>

			<Box
				sx={{
					mt: 'auto',
					position: 'relative',
					height: `${heightPercentage}%`,
					transition: 'all 0.3s ease-out',
					borderBottom: '1px solid #DBDBDB',
					'&:hover': {
						transform: isTallestBar ? 'scaleY(1.02)' : 'scaleY(1.08)', // Scale up slightly
						transformOrigin: 'bottom',
					},
				}}
			>
				<div className="relative h-full w-full">
					{animatedValue > 0 && (
						<>
							<div
								className="absolute inset-0 transition-all duration-700 ease-out"
								style={{
									background: '#BEBEF4',
									clipPath: 'polygon(0 0, 80% 0, 100% 100%, 0 100%)',
								}}
							/>
							<div
								className="absolute inset-0 transition-all duration-700 ease-out"
								style={{
									background: '#9690DF',
									clipPath: 'polygon(100% 100%, 100% 20%, 80% 0,80% 100%)',
								}}
							/>
						</>
					)}
				</div>
			</Box>
		</div>
	);
};

// Update the FunnelChart component
const FunnelChart: React.FC<FunnelChartProps> = ({ data, graphTitle = 'Title' }) => {
	const maxValue = Math.max(...data.map(item => item.value));

	return (
		<div className="max-w-11/12 mx-auto max-w-[1440px]">
			<div className="">
				<div className="relative flex h-full min-h-[350px]">
					{data?.map((item: DataItem, index: number) => <FunnelBar key={index} item={item} maxValue={maxValue} />)}
				</div>
			</div>

			<div className="text-center text-sm md:text-base font-[500] text-[#28293D] mt-5 italic">{graphTitle}</div>
		</div>
	);
};

const FunnelStageGraph: React.FC = () => {
	const { id } = useParams();
	const [assessmentData, setAssessmentData] = useState([
		{ label: 'Invited', value: 0 , styleMatch:true },
		{ label: 'Assessment started', value: 0 },
		{ label: 'Assessment completed', value: 0 },
		{ label: 'Qualified', value: 0,styleMatch:true },
	]);

	const [hiringData, setHiringData] = useState([
		{ label: 'Selected', value: 0 },
		{ label: 'Interviewed', value: 0 },
		{ label: 'Hired', value: 0 },
	]);

	const {
		isLoading: isAssessmentLoading,
		isFetching: isAssessmentFetching,
		isError: isAssessmentError,
	} = useGetAllHiringAssessments(id || '', data => {
		console.log('oncomplete is working', data);
		const hiringAssessmentData = data?.hiringAssessmentList[0];

		setAssessmentData([
			{ label: 'Invited', value: hiringAssessmentData?.invitedCandidates || 0, styleMatch:true },
			{ label: 'Assessment started', value: hiringAssessmentData?.ongoingAssessmentCandidates || 0 },
			{ label: 'Assessment completed', value: hiringAssessmentData?.assessmentCompletedCandidates || 0 },
			{ label: 'Qualified', value: hiringAssessmentData?.allQualifiedApplications || 0, styleMatch:true },
		]);

		setHiringData([
			{ label: 'Selected', value: hiringAssessmentData?.candidatesInPipeline || 0 },
			{ label: 'Interviewed', value: hiringAssessmentData?.candidatesInterviewed || 0 },
			{ label: 'Hired', value: hiringAssessmentData?.candidatesHired || 0 },
		]);
	});

	return (
		<div className="w-full mx-auto bg-gray-50">
			<div className="rounded-[8px] bg-[#FFF] py-3 md:py-7 px-2 md:px-10 relative">
				{isAssessmentLoading || isAssessmentFetching ? (
					<div className="absolute top-2 md:top-4 right-2 md:right-4 max-h-[24px] max-w-[24px] h-[19px] w-[19px]">
						<Spinner size="20px" className="mt-[-2px]" />
					</div>
				) : isAssessmentError ? (
					<div className="absolute top-2 md:top-4 right-2 md:right-4 max-h-[24px] max-w-[24px] h-[19px] w-[19px]">
						<MdOutlineErrorOutline color="red" />
					</div>
				) : null}
				<FunnelChart data={assessmentData} graphTitle="Candidate assessment stage" />
			</div>
			<div className="mt-8 rounded-[8px] bg-[#FFF] py-3 md:py-7 px-2 md:px-10 relative">
				{isAssessmentLoading || isAssessmentFetching ? (
					<div className="absolute top-2 md:top-4 right-2 md:right-4 max-h-[24px] max-w-[24px] h-[19px] w-[19px]">
						<Spinner size="20px" className="mt-[-2px]" />
					</div>
				) : null}
				<FunnelChart data={hiringData} graphTitle="Candidate selected stage" />
			</div>
		</div>
	);
};

export default FunnelStageGraph;
